import type { EaseTypeImpl } from '../types';
import { catmullRomWeightedDual, cubicBezier, sampleToCubicBezier } from '@use-gpu/core';
import { quat } from 'gl-matrix';
import { for4, zip4 } from './zip';

export const EaseQuat: EaseTypeImpl<quat> = {
  spline: (a: quat, b: quat, knots?: quat[]) => {
    if (knots) {
      const [k1, k2] = knots;
      if (!k1 || !k2) throw new Error("Spline ease needs 2 knots");

      // Relative to a
      const ak1 = quat.clone(k1);
      const ak2 = quat.clone(k2);
      const ab = quat.clone(b);

      quat.slerp(ak1, a, k1, 1);
      quat.slerp(ak2, ak1, k2, 1);
      quat.slerp(ab, ak2, b, 1);

      return (target: quat, t: number) => {
        zip4(target, a, k1, k2, b,
          (a: number, k1: number, k2: number, b: number) => cubicBezier(t, a, k1, k2, b)
        );
        quat.normalize(target, target);
        return target;
      };
    }
    else {
      return (target: quat, t: number) => quat.slerp(target, a, b, t);
    }
  },
  auto: (a: quat, b: quat, c: quat, d: quat) => {
    const l1 = EaseQuat.measure(a, b);
    const l2 = EaseQuat.measure(b, c);
    const l3 = EaseQuat.measure(c, d);

    const ba = quat.clone(a);
    const bc = quat.clone(c);
    const bd = quat.clone(d);

    quat.slerp(ba, b, a, 1);
    quat.slerp(bc, b, c, 1);
    quat.slerp(bd, b, d, 1);

    const k1s: number[] = [];
    const k2s: number[] = [];

    for4(ba, b, bc, bd,
      (a: number, b: number, c: number, d: number) => {
        const [,k1,k2] = sampleToCubicBezier((t: number) => catmullRomWeightedDual(t, a, b, c, d, l1, l2, l3))
        k1s.push(k1);
        k2s.push(k2);
      }
    );

    const k1 = quat.clone(k1s as quat);
    const k2 = quat.clone(k2s as quat);

    quat.normalize(k1, k1);
    quat.normalize(k2, k2);

    return [k1, k2];
  },
  lerp: (target: quat, a: quat, b: quat, t: number) => {
    return quat.slerp(target, a, b, t);
  },
  measure: (a: quat, b: quat) => {
    return quat.getAngle(a, b);
  },
};
